import { useEffect, useState } from "react"
import { Heart, AlertTriangle, Trophy, TrendingUp, Phone, Loader2, ChevronRight, Zap, Users } from "lucide-react"
import { studentService, type StudentDashboardData } from "@/services/studentService"
import { useApp } from "@/App"
import { useStudentNavigation } from "@/contexts/StudentNavigationContext"

const TIPS = [
  "Never share your password — not even with friends.",
  "Think before you post: once online, it's hard to take back.",
  "If something feels wrong online, tell a trusted adult.",
  "Treat others online the way you'd like to be treated.",
  "Check your privacy settings on any new app before using it.",
]

export function StudentHome() {
  const [data, setData]       = useState<StudentDashboardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  const [tipIdx, setTipIdx]   = useState(0)
  const { user }              = useApp()
  const { setCurrentPage }    = useStudentNavigation()
  const [dm, setDm]           = useState(() => localStorage.getItem('studentDarkMode') === 'true')

  useEffect(() => {
    const handler = (e: Event) => setDm((e as CustomEvent<{ darkMode: boolean }>).detail.darkMode)
    window.addEventListener('studentDarkModeChanged', handler)
    return () => window.removeEventListener('studentDarkModeChanged', handler)
  }, [])

  useEffect(() => {
    studentService.getDashboard()
      .then(setData)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'An error occurred'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const t = setInterval(() => setTipIdx(i => (i + 1) % TIPS.length), 5000)
    return () => clearInterval(t)
  }, [])

  const getGreeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  const ADMIN_NAME_PATTERN = /^(system|super)\s*(admin)?$/i
  const storedUser = (() => { try { return JSON.parse(sessionStorage.getItem('user') || '{}') } catch { return {} } })()
  const rawName = (storedUser.name || user?.name || '').split(' ')[0]
  const firstName = ADMIN_NAME_PATTERN.test(storedUser.name || user?.name || '')
    ? storedUser.email?.split('@')[0] || user?.email?.split('@')[0] || ''
    : rawName || storedUser.email?.split('@')[0] || user?.email?.split('@')[0] || ''

  const wellnessScore = data?.wellness.score ?? 0
  const wellness = wellnessScore >= 7
    ? { dot: 'bg-emerald-400', ring: 'ring-emerald-300', label: 'Great!', plain: 'Your browsing habits look great keep it up!'  }
    : wellnessScore >= 4
    ? { dot: 'bg-amber-400',   ring: 'ring-amber-300',   label: 'Good',   plain: "A few things to watch, but you're doing well." }
    : { dot: 'bg-rose-500',    ring: 'ring-rose-300',    label: 'Watch',  plain: "Let's work on better online habits together."  }

  const days = data?.weeklyProgress.days ?? []

  const streak = (() => {
    if (days.length === 0) return 0
    let count = 0
    for (let i = days.length - 1; i >= 0; i--) {
      if (days[i].points > 0) count++
      else break
    }
    return count
  })()

  const glassCard = dm
    ? "bg-white/10 backdrop-blur-sm rounded-2xl border border-white/10 shadow-md"
    : "bg-white/75 backdrop-blur-sm rounded-2xl border border-white/60 shadow-md"

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/60 backdrop-blur-sm border border-white/60 flex items-center justify-center animate-pulse">
            <Loader2 className="w-6 h-6 text-indigo-500 animate-spin" />
          </div>
          <span className={`text-sm ${dm ? 'text-white/60' : 'text-gray-600'}`}>Loading your dashboard…</span>
        </div>
      </div>
    )
  }

  const maxPts = Math.max(...days.map(d => d.points), 1)

  return (
    <div className="px-4 py-5 space-y-4">
      {error && (
        <div className="bg-amber-50/80 backdrop-blur-sm border border-amber-200/70 text-amber-700 px-4 py-3 rounded-2xl text-sm">
          Using cached data — live sync unavailable
        </div>
      )}

      {/* ── Hero card ── */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-indigo-700 to-blue-800 p-5 text-white shadow-xl shadow-indigo-300/40">
        <div className="absolute -top-10 -right-10 w-36 h-36 bg-white/10 rounded-full pointer-events-none" />
        <div className="absolute -bottom-8 -left-6 w-28 h-28 bg-white/5  rounded-full pointer-events-none" />
        <div className="relative">
          <h1 className="text-2xl font-bold mt-0.5 mb-5 capitalize">{getGreeting()}, {firstName} 👋</h1>
          <div className="flex items-center gap-4 mb-5">
            <div className={`relative flex-shrink-0 w-16 h-16 rounded-full bg-white/15 ring-2 ${wellness.ring} flex flex-col items-center justify-center gap-1`}>
              <div className={`w-5 h-5 rounded-full ${wellness.dot}`} />
              <span className="text-[9px] font-bold text-white leading-none">{wellness.label}</span>
            </div>
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <Heart className="w-4 h-4 text-rose-300" />
                <span className="text-sm font-semibold">Wellness</span>
              </div>
              <p className="text-indigo-100 text-xs leading-relaxed max-w-[200px]">{wellness.plain}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <div className="text-lg font-bold">{data?.activity.points ?? 0}</div>
              <div className="text-[10px] text-indigo-200 mt-0.5">Points</div>
            </div>
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <div className="text-lg font-bold">#{data?.rank.position ?? '—'}</div>
              <div className="text-[10px] text-indigo-200 mt-0.5">Class Rank</div>
            </div>
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <div className="text-lg font-bold">{data?.activity.screenTime ?? '0h'}</div>
              <div className="text-[10px] text-indigo-200 mt-0.5">Online Today</div>
            </div>
            <div className="bg-white/10 rounded-xl p-2.5 text-center">
              <div className="text-lg font-bold">{streak > 0 ? `${streak}d` : '—'}</div>
              <div className="text-[10px] text-indigo-200 mt-0.5">🔥 Streak</div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Welcome card ── */}
      {!error && (data?.activity.points ?? 0) === 0 && (
        <div className={`${glassCard} px-4 py-3 flex items-center gap-3`}>
          <span className="text-2xl flex-shrink-0">🎉</span>
          <div>
            <p className={`font-semibold text-sm ${dm ? 'text-indigo-300' : 'text-indigo-800'}`}>Welcome to ShieldED Haven!</p>
            <p className={`text-xs mt-0.5 ${dm ? 'text-indigo-400' : 'text-indigo-500'}`}>Play games and complete activities to earn your first points.</p>
          </div>
        </div>
      )}

      {/* ── Crisis help banner ── */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => setCurrentPage('safety')}
        onKeyDown={e => e.key === 'Enter' && setCurrentPage('safety')}
        className={`w-full flex items-center justify-between backdrop-blur-sm rounded-2xl px-4 py-3 cursor-pointer transition-colors select-none ${
          dm
            ? 'bg-rose-900/40 border border-rose-500/30 hover:bg-rose-900/60'
            : 'bg-rose-50/80 border border-rose-200/70 hover:bg-rose-100/80'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${dm ? 'bg-rose-800/60' : 'bg-rose-100/80'}`}>
            <Phone className="w-4 h-4 text-rose-400" />
          </div>
          <div>
            <p className={`font-semibold text-sm ${dm ? 'text-rose-300' : 'text-rose-800'}`}>Need Immediate Help?</p>
            <p className={`text-xs ${dm ? 'text-rose-400' : 'text-rose-500'}`}>Crisis support is available 24/7</p>
          </div>
        </div>
        <ChevronRight className={`w-4 h-4 flex-shrink-0 ${dm ? 'text-rose-500' : 'text-rose-400'}`} />
      </div>

      {/* ── Level & XP ── */}
      <div className={`${glassCard} p-4`}>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dm ? 'bg-amber-900/40' : 'bg-amber-100/80'}`}>
              <Zap className="w-4 h-4 text-amber-500" />
            </div>
            <div>
              <p className={`font-semibold text-sm leading-tight ${dm ? 'text-white' : 'text-gray-900'}`}>Level {data?.activity.level ?? 1}</p>
              <p className={`text-xs leading-tight ${dm ? 'text-white/50' : 'text-gray-500'}`}>{data?.activity.levelName ?? 'Digital Beginner'}</p>
            </div>
          </div>
          <span className={`text-xs px-2.5 py-1 rounded-full border ${dm ? 'text-white/50 bg-white/10 border-white/10' : 'text-gray-500 bg-white/60 border-white/60'}`}>
            {data?.activity.todayEvents ?? 0} activities today
          </span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${dm ? 'bg-white/10' : 'bg-gray-100/80'}`}>
          <div
            className="h-full bg-gradient-to-r from-amber-400 to-orange-400 rounded-full transition-all duration-700"
            style={{ width: `${Math.min(((data?.activity.points ?? 0) % 250) / 2.5, 100)}%` }}
          />
        </div>
        <p className={`text-[10px] mt-1.5 ${dm ? 'text-white/40' : 'text-gray-500'}`}>{data?.activity.points ?? 0} total safety points earned</p>
      </div>

      {/* ── Class rank ── */}
      <div className={`${glassCard} p-4`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${dm ? 'bg-purple-900/40' : 'bg-purple-100/80'}`}>
            <Trophy className="w-5 h-5 text-purple-500" />
          </div>
          <div className="flex-1">
            <div className="flex items-baseline gap-1.5">
              <span className={`text-2xl font-bold ${dm ? 'text-white' : 'text-gray-900'}`}>#{data?.rank.position ?? '—'}</span>
              <span className={`text-sm ${dm ? 'text-white/50' : 'text-gray-500'}`}>of {data?.rank.totalClassmates ?? '—'} students</span>
            </div>
            <p className={`text-xs mt-0.5 ${dm ? 'text-white/50' : 'text-gray-600'}`}>
              {data?.rank.percentile !== undefined
                ? `You're in the top ${100 - data.rank.percentile}% of your class!`
                : 'Keep earning points to improve your rank'}
            </p>
          </div>
          {data?.rank.position === 1 && <span className="text-2xl flex-shrink-0">🏆</span>}
        </div>
      </div>

      {/* ── Weekly progress ── */}
      <div className={`${glassCard} p-4`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${dm ? 'bg-blue-900/40' : 'bg-blue-100/80'}`}>
              <TrendingUp className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <p className={`font-semibold text-sm leading-tight ${dm ? 'text-white' : 'text-gray-900'}`}>This Week</p>
              <p className={`text-xs leading-tight ${dm ? 'text-white/50' : 'text-gray-500'}`}>Daily safety points</p>
            </div>
          </div>
          <span className="text-lg font-bold text-blue-400">
            {(data?.weeklyProgress.totalPoints ?? 0) > 0 ? `+${data!.weeklyProgress.totalPoints}` : '0'} pts
          </span>
        </div>

        {days.length > 0 ? (
          <div className="flex items-end gap-1.5 h-16">
            {days.map((day, i) => {
              const pct     = Math.max((day.points / maxPts) * 100, 5)
              const isToday = i === days.length - 1
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                  <div
                    className={`w-full rounded-t-lg transition-all duration-500 ${
                      isToday ? 'bg-indigo-500' : day.points > 0 ? 'bg-indigo-300/70' : dm ? 'bg-white/10' : 'bg-gray-200/60'
                    }`}
                    style={{ height: `${pct}%` }}
                  />
                  <span className={`text-[9px] font-medium ${isToday ? 'text-indigo-400' : dm ? 'text-white/40' : 'text-gray-500'}`}>
                    {day.day}
                  </span>
                </div>
              )
            })}
          </div>
        ) : (
          <div className={`h-16 flex items-center justify-center text-xs ${dm ? 'text-white/40' : 'text-gray-500'}`}>No activity this week yet</div>
        )}
      </div>

      {/* ── Flagged events ── */}
      {data?.wellness.recentFlaggedEvents !== undefined && data.wellness.recentFlaggedEvents > 0 && (
        <div className={`backdrop-blur-sm rounded-2xl px-4 py-3 space-y-2 ${
          dm ? 'bg-orange-900/30 border border-orange-500/20' : 'bg-orange-50/80 border border-orange-200/70'
        }`}>
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-orange-500 flex-shrink-0" />
            <p className={`text-sm ${dm ? 'text-orange-300' : 'text-orange-700'}`}>
              <span className="font-semibold">{data.wellness.recentFlaggedEvents} flagged event{data.wellness.recentFlaggedEvents > 1 ? 's' : ''}</span>{' '}
              this week, keep making good choices!
            </p>
          </div>
          <div className={`flex items-center gap-2 pt-1 border-t ${dm ? 'border-orange-500/20' : 'border-orange-100/80'}`}>
            <Users className="w-3.5 h-3.5 text-orange-400 flex-shrink-0" />
            <p className={`text-xs ${dm ? 'text-orange-400' : 'text-orange-600'}`}>Your parent or guardian has been kept informed.</p>
          </div>
        </div>
      )}

      {/* ── Tip carousel ── */}
      <div className={`${glassCard} p-4`}>
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${dm ? 'bg-emerald-900/40' : 'bg-emerald-100/80'}`}>
            <span className="text-sm">💡</span>
          </div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${dm ? 'text-white/50' : 'text-gray-500'}`}>Tip of the Moment</p>
        </div>
        <p className={`text-sm leading-relaxed ${dm ? 'text-white/80' : 'text-gray-800'}`}>{TIPS[tipIdx]}</p>
        <div className="flex items-center gap-1.5 mt-3 justify-center">
          {TIPS.map((_, i) => (
            <button
              key={i}
              onClick={() => setTipIdx(i)}
              style={{ border: 'none', padding: 0, background: 'none', cursor: 'pointer' }}
              className={`rounded-full transition-all duration-300 ${i === tipIdx ? 'bg-indigo-500 w-4 h-1.5' : dm ? 'bg-white/20 w-1.5 h-1.5' : 'bg-gray-300/80 w-1.5 h-1.5'}`}
              aria-label={`Tip ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
